import { useMemo } from 'react'

import { useApiGetCommonConfig } from 'api/kinerja/kepala-sekolah/common'
import useToggleSdkContext from 'utils/useToogleSdk'
import { isDateBetweenDates } from 'utils/datetime'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { TResponseCommonConfig } from 'api/kinerja/kepala-sekolah/common'

type TProps = {
  today?: Date
  contextYear?: number
}

type TResponse = {
  config?: TResponseCommonConfig['data']
  isLoading: boolean
  isFetched: boolean
  isSuccess: boolean
  isError: boolean
  isWithinAllowedRange: boolean
  isAfterAllowedDate: boolean
}

/**
 * Custom hook to determine the accessibility of the "Penetapan Tahunan" feature
 * based on the current date and configuration settings.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Date} [params.today=new Date()] - The current date, defaults to today's date.
 * @param {number} params.contextYear - The context year, defaults to context's period year from usePeriodContext. Riwayat page should utilize this prop.
 *
 * @returns {Object} The response object containing the configuration and accessibility status.
 * @returns {Object} return.config - The configuration data for the "Penetapan Tahunan Dinas" feature.
 * @returns {boolean} return.isLoading - Indicates if the configuration data is still loading.
 * @returns {boolean} return.isFetched - Indicates if the configuration data has been fetched.
 * @returns {boolean} return.isSuccess - Indicates if the configuration data was successfully fetched.
 * @returns {boolean} return.isError - Indicates if there was an error fetching the configuration data.
 * @returns {boolean} return.isWithinAllowedRange - Indicates if the current date is within the allowed date range.
 * @returns {boolean} return.isAfterAllowedDate - Indicates if the current date is after the allowed start date.
 */
const useRiwayatTahunanAccessibility = ({
  today = new Date(),
  contextYear,
}: TProps): TResponse => {
  const toggleValue = useToggleSdkContext()

  // Get the current context year, eg. from ganti periode feature
  const { periodYear: predefinedContextYear } = usePeriodContext()

  // Can be overridden by the context year from the props
  const finalContextYear = contextYear || predefinedContextYear

  // Check if the toggle is enabled
  const isEnabled = toggleValue?.['kinerja-dinas-penetapan-tahunan-enabled']

  const {
    data: { data: config } = {},
    isLoading,
    isFetched,
    isSuccess,
    isError,
  } = useApiGetCommonConfig(
    { key: 'PENETAPAN_TAHUNAN_TIME_RANGE' },
    { enabled: isEnabled }
  )

  const { isWithinAllowedRange, isAfterAllowedDate } = useMemo(() => {
    // Calculate the next period year
    const nextContextYear = finalContextYear + 1

    // Check if the common config is still loading or failed to load
    if (!isEnabled || isLoading || isError)
      return { isWithinAllowedRange: false, isAfterAllowedDate: false }

    // Define the start date for Penetapan Tahunan Dinas, default to December 3rd
    const rangeDateStart = new Date(
      finalContextYear,
      (config?.startDate?.month || 12) - 1, // Month is 0-based
      config?.startDate?.day || 3 // Day is 1-based
    )

    // Define the end date for Penetapan Tahunan Dinas, default to June 30th of the next year
    const rangeDateEnd = new Date(
      nextContextYear,
      (config?.endDate?.month || 6) - 1, // Month is 0-based
      config?.endDate?.day || 30 // Day is 1-based
    )

    // Check if today is within the allowed date range
    const isWithinAllowedRange = isDateBetweenDates(
      today,
      rangeDateStart,
      rangeDateEnd
    )

    return {
      isWithinAllowedRange: isWithinAllowedRange,
      isAfterAllowedDate: today >= rangeDateStart,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextYear, isLoading, isSuccess, isEnabled, config])

  return {
    config,
    isLoading,
    isFetched,
    isSuccess,
    isError,
    isWithinAllowedRange,
    isAfterAllowedDate,
  }
}

export default useRiwayatTahunanAccessibility
